import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardMedia,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  PhotoCamera as PhotoIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

interface PhotoUploadProps {
  open: boolean;
  onClose: () => void;
  shipmentId: string | null;
  locationId?: string;
}

interface UploadedPhoto {
  id: string;
  file_name: string;
  file_path: string;
  photo_type: string;
  description: string;
  uploaded_at: string;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  open,
  onClose,
  shipmentId,
  locationId,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [photoType, setPhotoType] = useState("pickup");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [uploadedPhotos, setUploadedPhotos] = useState<UploadedPhoto[]>([]);

  useEffect(() => {
    if (open && shipmentId) {
      fetchPhotos();
    }
  }, [open, shipmentId]);

  const fetchPhotos = async () => {
    try {
      const token = localStorage.getItem("driver_token");
      const response = await fetch(
        `${API_URL}/api/shipments/${shipmentId}/photos`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const photos = await response.json();
        setUploadedPhotos(photos);
      }
    } catch (error) {
      console.error("Error fetching photos:", error);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !shipmentId) return;

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("photo", selectedFile);
      formData.append("photo_type", photoType);
      formData.append("description", description);
      if (locationId) {
        formData.append("location_id", locationId);
      }

      const token = localStorage.getItem("driver_token");
      const response = await fetch(
        `${API_URL}/api/shipments/${shipmentId}/photos`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const uploadedPhoto = await response.json();
        setUploadedPhotos([...uploadedPhotos, uploadedPhoto]);
        setSelectedFile(null);
        setPreview(null);
        setDescription("");
        setPhotoType("pickup");
      } else {
        throw new Error("Failed to upload photo");
      }
    } catch (error) {
      setError("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId: string) => {
    try {
      const token = localStorage.getItem("driver_token");
      const response = await fetch(
        `${API_URL}/api/shipments/photos/${photoId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setUploadedPhotos(
          uploadedPhotos.filter((photo) => photo.id !== photoId)
        );
      }
    } catch (error) {
      console.error("Error deleting photo:", error);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    setDescription("");
    setPhotoType("pickup");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Photo</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Upload New Photo
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Photo Type</InputLabel>
                <Select
                  value={photoType}
                  onChange={(e) => setPhotoType(e.target.value)}
                  label="Photo Type"
                >
                  <MenuItem value="pickup">Pickup</MenuItem>
                  <MenuItem value="dropoff">Dropoff</MenuItem>
                  <MenuItem value="damage">Damage</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />

              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Select Photo
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>

              {preview && (
                <Card sx={{ mb: 2 }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={preview}
                    alt="Preview"
                    sx={{ objectFit: "cover" }}
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {selectedFile?.name}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              <Button
                variant="contained"
                startIcon={<PhotoIcon />}
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                fullWidth
              >
                {uploading ? <CircularProgress size={20} /> : "Upload Photo"}
              </Button>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Uploaded Photos
              </Typography>

              {uploadedPhotos.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No photos uploaded yet
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {uploadedPhotos.map((photo) => (
                    <Card key={photo.id}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {photo.photo_type}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDelete(photo.id)}
                          >
                            Delete
                          </Button>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {photo.file_name}
                        </Typography>
                        {photo.description && (
                          <Typography variant="body2">
                            {photo.description}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {new Date(photo.uploaded_at).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoUpload;
